import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d4rdl01ab95mriqdiqm0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-32 bg-white">
      <div className="max-w-7xl mx-auto px-8">
        <div className="bg-gray-900 rounded-3xl p-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div className="space-y-8">
              <div>
                <h2 className="text-6xl font-thin text-white mb-6">Get In Touch</h2>
                <p className="text-base text-gray-400 leading-relaxed">
                  Have questions about our VPN service? We're here to help. Reach out to us and we'll get back to you as soon as possible.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form>
                <div>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your Name"
                    required
                    className="w-full bg-transparent border-b border-gray-700 text-white text-sm py-3 focus:border-teal-500 focus:outline-none transition-colors placeholder-gray-500"
                  />
                </div>
                <div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Your Email"
                    required
                    className="w-full bg-transparent border-b border-gray-700 text-white text-sm py-3 focus:border-teal-500 focus:outline-none transition-colors placeholder-gray-500"
                  />
                </div>
                <div>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Phone Number (Optional)"
                    className="w-full bg-transparent border-b border-gray-700 text-white text-sm py-3 focus:border-teal-500 focus:outline-none transition-colors placeholder-gray-500"
                  />
                </div>
                <div>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Your Message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full bg-transparent border-b border-gray-700 text-white text-sm py-3 focus:border-teal-500 focus:outline-none transition-colors placeholder-gray-500 resize-none"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-2">{formData.message.length}/500 characters</p>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-teal-500 text-white px-8 py-4 rounded-full text-sm font-semibold hover:bg-teal-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
                {submitStatus === 'success' && (
                  <p className="text-teal-400 text-sm text-center">Message sent successfully!</p>
                )}
                {submitStatus === 'error' && (
                  <p className="text-red-400 text-sm text-center">Failed to send message. Please try again.</p>
                )}
              </form>
            </div>

            <div className="space-y-12">
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-500 mb-6">CONTACT INFORMATION</p>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-teal-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-teal-400 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400 mb-1">Phone</p>
                      <a href="tel:+18444913439" className="text-lg text-white hover:text-teal-400 transition-colors">
                        +1 8444913439
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-purple-400 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400 mb-1">Email</p>
                      <a href="mailto:contact@jccabralsrl.com" className="text-lg text-white hover:text-teal-400 transition-colors">
                        contact@jccabralsrl.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-orange-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-orange-400 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400 mb-1">Location</p>
                      <p className="text-base text-gray-300 leading-relaxed">
                        JOSE MARIA URIBURU 4780<br />
                        Formosa, P3600HFR<br />
                        Argentina
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-8 border-t border-gray-800">
                <p className="text-xs uppercase tracking-wider text-gray-500 mb-6">FOLLOW US</p>
                <div className="flex items-center space-x-4">
                  <a href="#" className="w-12 h-12 bg-gray-800 rounded-full flex items-center justify-center hover:bg-teal-500 transition-colors cursor-pointer">
                    <i className="ri-linkedin-fill text-white text-xl"></i>
                  </a>
                  <a href="#" className="w-12 h-12 bg-gray-800 rounded-full flex items-center justify-center hover:bg-teal-500 transition-colors cursor-pointer">
                    <i className="ri-twitter-x-fill text-white text-xl"></i>
                  </a>
                  <a href="#" className="w-12 h-12 bg-gray-800 rounded-full flex items-center justify-center hover:bg-teal-500 transition-colors cursor-pointer">
                    <i className="ri-facebook-fill text-white text-xl"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
